import {
  $ArithmeticOperation,
  $ModuleArea,
  ArithmeticOperation,
  GymMode,
  ModuleArea,
} from '@suntex.fim/enums';

import { ModuleSlug, NodeModel } from '../../../types';
import { buildGymModeNode } from '../../utils/buildGymNode';

const moduleAreaDesc = $ModuleArea.describe(ModuleArea.K2World);

export const k2Node: NodeModel = {
  tag: moduleAreaDesc.slug,
  info: {
    title: moduleAreaDesc.label,
    summary: 'K-2, Pre-K World, and First in Reading®',
  },
  label: {
    aria: moduleAreaDesc.label,
    class: 'MainLabel',
    frame: 7,
  },
  color: moduleAreaDesc.color,
  children: [
    {
      tag: 'numbers',
      icon: 'games/numbers-k2',
      label: 'Numbers',
      color: 0xff6633,
      children: [
        {
          tag: 'jtf',
          icon: 'modules/jtf',
          label: 'JTF',
          color: 0x663399,
          children: [
            ArithmeticOperation.Addition,
            ArithmeticOperation.Subtraction,
          ]
            .map((op) => $ArithmeticOperation.describe(op))
            .map((op) => ({
              moduleSlug: `jtf-k2-${op.slug}` as ModuleSlug,
              icon: `operations/${op.slug}`,
              label: {
                aria: op.label,
              },
              color: op.color,
              filtered: {
                icon: `alts/jtf-${op.slug}`,
                label: `K2 JTF\n${op.label}`,
                color: $ModuleArea.describe(ModuleArea.JustTheFacts).color,
              },
            })),
        },
        {
          ...buildGymModeNode(GymMode.K2),
          tag: 'gym',
          label: 'K2 Gyms',
        },
        {
          moduleSlug: '3-to-9-add',
          icon: 'games/3-to-9-add',
          label: '3-to-9\nAdd',
          color: 0xffcc00,
        },
        {
          moduleSlug: '3-to-9-sub',
          icon: 'games/3-to-9-sub',
          label: '3-to-9\nSubtract',
          color: 0xbb77cc,
        },
        {
          moduleSlug: 'k2-mol',
          icon: 'games/k2-mol',
          label: 'K2 More or Less',
          color: 0x66ccff,
        },
        {
          moduleSlug: 'k2-skip-game',
          icon: 'games/k2-skip-game',
          label: 'K2 Skip Game',
          color: 0xcc33ff,
        },
        {
          moduleSlug: 'k2-how-many',
          icon: 'games/k2-how-many',
          label: 'K2 How Many',
          color: 0x00ad00,
        },
        {
          moduleSlug: 'k2-misfits-num',
          icon: 'games/k2-misfits-num',
          label: 'K2 Misfits\nNumbers',
          color: 0x9999ff,
        },
        {
          moduleSlug: 'k2-undo',
          icon: 'games/k2-undo',
          label: 'K2 Undo \n2-10',
          color: 0x99cc00,
        },
        {
          tag: 'five-at-a-time',
          icon: 'games/k2-five-at-a-time',
          label: 'K2 Five at a Time',
          color: 0x00cbaa,
          children: [
            {
              moduleSlug: 'k2-five-at-a-time',
              moduleVariant: 0,
              icon: 'games/five-at-a-time',
              label: 'Five at a Time\nNewb',
              color: 0x22cb73,
            },
            {
              moduleSlug: 'k2-five-at-a-time',
              moduleVariant: 1,
              icon: 'games/five-at-a-time',
              label: 'Five at a Time\nElite',
              color: 0x009f7a,
            },
          ],
        },
        {
          moduleSlug: 'k2-four-at-a-time',
          icon: 'games/k2-four-at-a-time',
          label: 'K2 4 At a Time',
          color: 0xff9933,
        },
        {
          moduleSlug: 'k2-before-and-after',
          icon: 'games/k2-before-and-after',
          label: 'K2 Before & After',
          color: 0x010032,
        },
      ],
    },
    {
      tag: 'pre-k',
      label: 'Pre-K',
      color: 0x00dcc8,
      children: [
        {
          moduleSlug: 'pk-three-at-a-time',
          icon: 'games/pk-three-at-a-time',
          label: 'Pre-K 3 At a Time',
          color: 0xff6499,
        },
        {
          moduleSlug: 'pk-how-many',
          icon: 'games/pk-how-many',
          label: 'Pre-K How Many',
          color: 0xffc900,
        },
        {
          moduleSlug: 'pk-before-and-after',
          icon: 'games/pk-before-and-after',
          label: 'Pre-K Before & After',
          color: 0x37f000,
        },
        {
          moduleSlug: 'pk-mol',
          icon: 'games/pk-mol',
          label: 'Pre-K \nMore Or Less',
          color: 0x33b0ff,
        },
        {
          moduleSlug: 'pk-missing-icons',
          icon: 'games/pk-missing-icons',
          label: 'Pre-K \nMissing Icons',
          color: 0x3399ff,
        },
        {
          moduleSlug: 'link-n-lock-mosaic',
          icon: 'games/link-n-lock-mosaic',
          label: "Link 'N Lock\nMosaic",
          color: 0xff8471,
        },
        {
          moduleSlug: 'pk-memory-tiles',
          icon: 'games/pk-memory-tiles',
          label: 'Pre-K\nMemory Tiles',
          color: 0x9900cc,
        },
      ],
    },
    {
      tag: 'shapes',
      icon: 'games/shapes-k2',
      label: 'Shapes and Logic',
      color: 0xff9900,
      children: [
        {
          moduleSlug: 'jeweled-tiles',
          icon: 'games/jeweled-tiles',
          label: 'K2 Jeweled \nTiles',
          color: 0x0033ff,
        },
        {
          moduleSlug: 'gems-to-zero',
          icon: 'games/gems-to-zero',
          label: 'K2 Gems \nTo Zero',
          color: 0x00d2ff,
        },
        {
          moduleSlug: 'link-n-lock-jewels',
          icon: 'games/link-n-lock-jewels',
          label: "Link 'N Lock\nJewels",
          color: 0x0099ff,
        },
        {
          moduleSlug: 'k2-jennies-jewels',
          icon: 'games/k2-jennies-jewels',
          label: "K2 Jennie's Jewels",
          color: 0x33dbe4,
        },
        {
          moduleSlug: 'k2-pundis-puzzle',
          icon: 'games/k2-pundis-puzzle',
          label: "K2 Pundi's Puzzle",
          color: 0x99cc00,
        },
        {
          moduleSlug: 'k2-triplets',
          icon: 'games/k2-triplets',
          label: 'K2 Triplets',
          color: 0xda2047,
        },
        {
          moduleSlug: 'see-3',
          icon: 'games/see-3',
          label: 'See 3',
          color: 0xff8800,
        },
        {
          moduleSlug: 'k2-rps',
          icon: 'games/k2-rps',
          label: 'K2 RPS Chess',
          color: 0x7bd3ff,
        },
        {
          moduleSlug: 'k2-misfits',
          icon: 'games/k2-misfits',
          label: 'K2 Misfits',
          color: 0xff3333,
        },
      ],
    },
    {
      tag: 'first-in-reading',
      label: 'First In Reading®',
      layout: 'clock',
      color: 0x5644da,
      children: [
        {
          tag: 'letters',
          label: {
            aria: 'Letters',
          },
          filtered: {
            label: 'Letters',
          },
          layout: 'clock',
          color: 0x3fb2ff,
          children: [
            {
              moduleSlug: 'abc',
              icon: 'games/abc',
              label: 'ABC',
              color: 0x3384ff,
            },
            {
              moduleSlug: 'letter-memory',
              icon: 'games/letter-memory',
              label: 'Letter\nMemory',
              color: 0x9900cc,
            },
            {
              moduleSlug: 'matching-letters',
              icon: 'games/matching-letters',
              label: 'Matching\nLetters',
              color: 0x9900cc,
            },
            {
              moduleSlug: 'alphabet',
              icon: 'games/alphabet',
              label: '\nAlphabet',
              color: 0xff3333,
            },
            {
              moduleSlug: 'sound-of-letters',
              icon: 'games/sound-of-letters',
              label: 'Sound of Letters',
              color: 0xff6633,
            },
            {
              moduleSlug: 'phonemes-alpha',
              icon: 'games/phonemes-alpha',
              label: 'Phonemes\nAlpha',
              color: 0xff66ff,
            },
            {
              moduleSlug: 'phonemes-pi',
              icon: 'games/phonemes-pi',
              label: 'Phonemes\nPi',
              color: 0x9912ff,
            },
            {
              moduleSlug: 'cb-sounds',
              icon: 'games/cb-sounds',
              label: 'CB Sounds',
              color: 0x94db00,
            },
            {
              moduleSlug: 'consonant-blends',
              icon: 'games/consonant-blends',
              label: 'Consonant Blends',
              color: 0x00ccff,
            },
          ],
        },
        {
          tag: 'words',
          label: {
            aria: 'Words',
          },
          filtered: {
            label: 'Words',
          },
          layout: 'clock',
          color: 0xff505a,
          children: [
            {
              moduleSlug: 'high-fq-words',
              icon: 'games/high-fq-words',
              label: '\nHigh FQ Words',
              color: 0xffa900,
            },
            {
              moduleSlug: 'rhyme-time',
              icon: 'games/rhyme-time',
              label: 'Rhyme Time',
              color: 0x33cc99,
            },
            {
              moduleSlug: 'change-one-letter',
              icon: 'games/change-one-letter',
              label: 'Change A Letter',
              color: 0x044e7b,
            },
            {
              moduleSlug: 'misfits-spelling',
              icon: 'games/misfits-spelling',
              label: 'Misfits\nSpelling',
              color: 0xff8300,
            },
            {
              moduleSlug: 'missing-letters',
              icon: 'games/missing-letters',
              label: 'Missing\nLetters',
              color: 0x000000,
            },
            {
              moduleSlug: 'word-wheels',
              icon: 'games/word-wheels',
              label: 'Word\nWheels',
              color: 0xff3333,
            },
            {
              moduleSlug: 'finding-words-4x4',
              icon: 'games/finding-words-4x4',
              label: 'Finding Words\n4x4',
              color: 0xff99cc,
            },
          ],
        },
        {
          tag: 'sentences',
          label: {
            aria: 'Sentences',
          },
          filtered: {
            label: 'Sentences',
          },
          layout: 'clock',
          color: 0x7331c6,
          children: [
            {
              moduleSlug: 'verb-tenses',
              icon: 'games/verb-tenses',
              label: 'Verb\nTenses',
              color: 0xffbc33,
            },
            {
              moduleSlug: 'building-sentences',
              icon: 'games/building-sentences',
              label: 'Building\nSentences',
              color: 0xed4d24,
            },
            {
              moduleSlug: 'write-a-sentence',
              icon: 'games/write-a-sentence',
              label: 'Write a\nSentence',
              color: 0xff0096,
            },
          ],
        },
      ],
    },
  ],
};
